/** One workspace panel/claim registry; only the actual active Workbench view selects a host. */
import { ChatContextReadError } from '@affine/core/blocksuite/ai/components/ai-chat-content/chat-context-manager';
import type {
  ChatContextManagerPort,
  ChatContextPickingControl,
  ChatContextSelection,
} from '@affine/core/blocksuite/ai/components/ai-chat-content/type';
import type { GlobalStateService } from '@affine/core/modules/storage/services/global';
import { showContextPickingScreen } from '@affine/core/modules/streamlain/copilot/context-picking-presence';
import type {
  WorkspaceContextIssue,
  WorkspaceViewRevision,
} from '@streamlain/copilot-realtime-contract';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  fromEvent,
  map,
  type Observable,
  takeUntil,
} from 'rxjs';

export interface ChatPanelHostScope {
  workspaceId: string;
  viewId: string;
}
export type ChatPanelHostIssue = 'inactive-view' | 'unavailable';
export type ChatPanelHostResult =
  | { ok: true }
  | { ok: false; reason: ChatPanelHostIssue };
export type ChatPanelClaimResult =
  | { ok: true; release: () => void }
  | { ok: false; reason: ChatPanelHostIssue };
export interface ChatPanelOverlayState {
  scope: ChatPanelHostScope | null;
  visible: boolean;
  content: 'workspace' | 'surface';
  surfaceAtBase: boolean;
}
interface Base {
  scope: ChatPanelHostScope;
  element: HTMLElement;
}
interface PanelBase extends Base {
  panel: HTMLElement | null;
}
interface Claim extends Base {
  content?: HTMLElement;
  parent: Node | null;
  nextSibling: Node | null;
}
interface PickingSource {
  scope: ChatPanelHostScope;
  sourceKey: string;
  generation: string;
  /** What the author last selected there outside picking, for a Content bound later. */
  followed: {
    revision: WorkspaceViewRevision;
    selection: ChatContextSelection;
  } | null;
}
interface ContextOwner {
  scope: ChatPanelHostScope;
  manager: ChatContextManagerPort;
}
interface ChatPanelHostState {
  panel: HTMLElement | null;
  readonly bases: PanelBase[];
  readonly claims: Claim[];
  attached: Claim | null;
  visible: boolean;
  returnFocus: Base | null;
  readonly claimed$: BehaviorSubject<boolean>;
  readonly overlay$: BehaviorSubject<ChatPanelOverlayState>;
  readonly picking$: BehaviorSubject<boolean>;
  readonly pickingSources: PickingSource[];
  readonly contextOwner$: BehaviorSubject<ContextOwner | null>;
}
const hostStates = new Map<string, ChatPanelHostState>();
let activeScope: ChatPanelHostScope | null = null;
let releasePickingPresence: (() => void) | null = null;
/** One presence while any view picks: the screen effect and the only Escape exit. */
function syncPickingPresence() {
  if (![...hostStates.values()].some(state => state.picking$.value)) {
    releasePickingPresence?.();
    releasePickingPresence = null;
    return;
  }
  if (releasePickingPresence) return;
  // Window capture runs before every document, dialog and editor handler.
  const escape = (event: KeyboardEvent) => {
    if (activeScope) consumeChatPanelPickingEscape(activeScope, event);
  };
  window.addEventListener('keydown', escape, true);
  const hideScreen = showContextPickingScreen(document);
  releasePickingPresence = () => {
    window.removeEventListener('keydown', escape, true);
    hideScreen();
  };
}
const sameScope = (
  a: ChatPanelHostScope | null,
  b: ChatPanelHostScope | null
) => a?.workspaceId === b?.workspaceId && a?.viewId === b?.viewId;
function getHostState(workspaceId: string): ChatPanelHostState {
  let state = hostStates.get(workspaceId);
  if (!state) {
    state = {
      panel: null,
      bases: [],
      claims: [],
      attached: null,
      visible: false,
      returnFocus: null,
      picking$: new BehaviorSubject(false),
      pickingSources: [],
      contextOwner$: new BehaviorSubject<ContextOwner | null>(null),
      claimed$: new BehaviorSubject(false),
      overlay$: new BehaviorSubject<ChatPanelOverlayState>({
        scope:
          activeScope?.workspaceId === workspaceId ? { ...activeScope } : null,
        visible: false,
        content: 'workspace',
        surfaceAtBase: false,
      }),
    };
    hostStates.set(workspaceId, state);
    state.picking$.subscribe(syncPickingPresence);
  }
  return state;
}
function belongsToScope(scope: ChatPanelHostScope, element: HTMLElement) {
  const view = element
    .closest('[data-workspace-view-id]')
    ?.getAttribute('data-workspace-view-id');
  return !view || view === scope.viewId;
}
function selectedClaim(state: ChatPanelHostState) {
  return (
    state.claims.findLast(
      claim =>
        sameScope(claim.scope, activeScope) &&
        claim.element.isConnected &&
        belongsToScope(claim.scope, claim.element)
    ) ?? null
  );
}
function nativeActiveElement(document: Document): HTMLElement | null {
  let element = document.activeElement;
  for (let depth = 0; depth < 32; depth++) {
    const inner = element?.shadowRoot?.activeElement;
    if (!inner) return element instanceof HTMLElement ? element : null;
    element = inner;
  }
  return null;
}
function composedParent(element: HTMLElement): HTMLElement | null {
  if (element.parentElement) return element.parentElement;
  const root = element.getRootNode();
  return root instanceof ShadowRoot && root.host instanceof HTMLElement
    ? root.host
    : null;
}
/** Only the native active ancestor chain is visited; never enumerate a view. */
export function blurChatPanelFocusWithin(element: HTMLElement) {
  const focused = nativeActiveElement(element.ownerDocument);
  let ancestor = focused;
  for (let depth = 0; ancestor && depth < 128; depth++) {
    if (ancestor === element) {
      focused?.blur();
      return;
    }
    ancestor = composedParent(ancestor);
  }
}
function validReturnFocus(scope: ChatPanelHostScope, element: HTMLElement) {
  if (!element.isConnected) return false;
  let ancestor: HTMLElement | null = element;
  let bound = false;
  for (let depth = 0; ancestor && depth < 128; depth++) {
    if (
      ancestor.hidden ||
      ancestor.inert ||
      ancestor.hasAttribute('data-copilot-ignore')
    )
      return false;
    if (!bound && ancestor.hasAttribute('data-workspace-view-id')) {
      if (
        ancestor.getAttribute('data-workspace-view-id') !== scope.viewId ||
        ancestor.getAttribute('data-workspace-id') !== scope.workspaceId
      )
        return false;
      bound = true;
    }
    ancestor = composedParent(ancestor);
  }
  return bound && !ancestor;
}
function restoreContent(claim: Claim) {
  if (!claim.content) return;
  blurChatPanelFocusWithin(claim.content);
  if (claim.parent)
    claim.parent.insertBefore(
      claim.content,
      claim.nextSibling?.parentNode === claim.parent ? claim.nextSibling : null
    );
  else claim.content.remove();
}
function attachPanelToActiveHost(
  workspaceId: string,
  state: ChatPanelHostState
) {
  const claim = selectedClaim(state);
  if (state.attached !== claim) {
    state.returnFocus = null;
    if (state.attached?.content) restoreContent(state.attached);
    state.attached = claim;
  }
  const base = state.bases.findLast(
    base => sameScope(base.scope, activeScope) && base.element.isConnected
  );
  const host =
    claim && !claim.content
      ? claim.element
      : base?.panel === state.panel && base?.panel
        ? base.element
        : undefined;
  const panel = state.panel;
  if (panel && (!host || (claim?.content && host === claim.element))) {
    blurChatPanelFocusWithin(panel);
    panel.remove();
  } else if (panel && host && panel.parentElement !== host) {
    blurChatPanelFocusWithin(panel);
    host.append(panel);
  }
  if (claim?.content && claim.content.parentElement !== claim.element)
    claim.element.append(claim.content);
  state.claimed$.next(!!claim);
  state.overlay$.next({
    scope: activeScope?.workspaceId === workspaceId ? { ...activeScope } : null,
    visible: activeScope?.workspaceId === workspaceId && state.visible,
    content: claim?.content ? 'surface' : 'workspace',
    surfaceAtBase: !!claim?.content && claim.element === base?.element,
  });
}
/**
 * Only the Workspace Workbench root calls this; individual base mounts never set focus.
 * Returns how many explicit selections the switch removed, so the caller can tell the user.
 */
export function setActiveChatPanelView(
  scope: ChatPanelHostScope | null
): number {
  if (sameScope(activeScope, scope)) return 0;
  const previous = activeScope;
  activeScope = scope ? { ...scope } : null;
  if (scope) getHostState(scope.workspaceId);
  let cleared = 0;
  for (const [workspaceId, state] of hostStates) {
    state.picking$.next(false);
    if (
      previous &&
      state.contextOwner$.value &&
      sameScope(state.contextOwner$.value.scope, previous)
    )
      cleared += state.contextOwner$.value.manager.invalidateScope(previous);
    state.visible = false;
    state.returnFocus = null;
    attachPanelToActiveHost(workspaceId, state);
  }
  return cleared;
}

/** Bind the actual Content manager, not another ContextValue in the host. */
export function registerChatPanelContextOwner(
  scope: ChatPanelHostScope,
  manager: ChatContextManagerPort
): () => void {
  const state = getHostState(scope.workspaceId);
  const owner = { scope: { ...scope }, manager };
  if (state.contextOwner$.value?.manager !== manager)
    state.picking$.next(false);
  state.contextOwner$.next(owner);
  // A selection made before the panel opened is its context from the start.
  for (const source of state.pickingSources)
    if (source.followed && sameScope(source.scope, scope))
      applyFollowed(manager, source.sourceKey, source.followed);
  return () => {
    if (state.contextOwner$.value !== owner) return;
    state.picking$.next(false);
    state.contextOwner$.next(null);
    // A native panel move disconnects/reconnects the same Content. Its pins
    // remain on that owner; only source/view loss invalidates references.
  };
}

/**
 * Settles once the Content manager of `scope` is bound, right away where it
 * is: a closed Copilot binds it when it opens.
 */
export function chatPanelContextOwnerReady(
  scope: ChatPanelHostScope,
  signal: AbortSignal
): Promise<void> {
  if (signal.aborted) return Promise.reject(signal.reason);
  const owner$ = getHostState(scope.workspaceId).contextOwner$.pipe(
    filter(owner => sameScope(owner?.scope ?? null, scope)),
    map(() => undefined)
  );
  return firstValueFrom(owner$.pipe(takeUntil(fromEvent(signal, 'abort'))), {
    defaultValue: undefined,
  }).then(() => signal.throwIfAborted());
}

export function setPickingEnabled(
  scope: ChatPanelHostScope,
  enabled: boolean
): ChatPanelHostResult {
  if (!sameScope(scope, activeScope))
    return { ok: false, reason: 'inactive-view' };
  const state = getHostState(scope.workspaceId);
  if (
    enabled &&
    (!sameScope(state.contextOwner$.value?.scope ?? null, scope) ||
      !state.pickingSources.some(source => sameScope(source.scope, scope)))
  )
    return { ok: false, reason: 'unavailable' };
  state.picking$.next(enabled);
  return { ok: true };
}

export function pickingState$(scope: ChatPanelHostScope): Observable<boolean> {
  return getHostState(scope.workspaceId).picking$.pipe(
    map(enabled => enabled && sameScope(scope, activeScope)),
    distinctUntilChanged()
  );
}

export type ChatPanelPickingEscapeEvent = Pick<
  KeyboardEvent,
  'key' | 'defaultPrevented' | 'preventDefault' | 'stopPropagation'
>;

export function consumeChatPanelPickingEscape(
  scope: ChatPanelHostScope,
  event: ChatPanelPickingEscapeEvent
): boolean {
  const state = getHostState(scope.workspaceId);
  if (
    event.key !== 'Escape' ||
    event.defaultPrevented ||
    !sameScope(scope, activeScope) ||
    !state.picking$.value
  )
    return false;
  setPickingEnabled(scope, false);
  event.preventDefault();
  event.stopPropagation();
  return true;
}

function applyFollowed(
  manager: ChatContextManagerPort,
  sourceKey: string,
  followed: NonNullable<PickingSource['followed']>
): { ok: true } | { ok: false; reason: WorkspaceContextIssue } {
  try {
    manager.applyAutomatic(sourceKey, followed.revision, {}, [
      followed.selection,
    ]);
    return { ok: true };
  } catch (error) {
    if (error instanceof ChatContextReadError)
      return { ok: false, reason: error.reason };
    throw error;
  }
}

/** Sources register only owner metadata. Hits go directly into Content. */
export function registerChatPanelPickingSource(
  scope: ChatPanelHostScope,
  sourceKey: string,
  generation: string
) {
  const state = getHostState(scope.workspaceId);
  const source: PickingSource = {
    scope: { ...scope },
    sourceKey,
    generation,
    followed: null,
  };
  state.pickingSources.push(source);
  let disposed = false;
  const admit = (
    selection: ChatContextSelection,
    picking: boolean
  ): ReturnType<ChatContextManagerPort['addExplicit']> => {
    const owner = state.contextOwner$.value;
    if (
      disposed ||
      (picking && !state.picking$.value) ||
      !sameScope(scope, activeScope) ||
      !owner ||
      !sameScope(owner.scope, scope)
    )
      return { ok: false, reason: 'unavailable' };
    if (
      !sameScope(selection.scope, scope) ||
      selection.sourceKey !== sourceKey ||
      selection.sourceGeneration !== generation ||
      selection.provenance !== 'explicit'
    )
      return { ok: false, reason: 'invalid' };
    return owner.manager.addExplicit(selection);
  };
  return {
    /** A user's hit while the selector is on. */
    pick: (selection: ChatContextSelection) => admit(selection, true),
    /** What a Copilot helper button hands over with its command. */
    hand: (selection: ChatContextSelection) => admit(selection, false),
    /**
     * Outside picking, what the author selects in the source is the chat's
     * automatic context: it replaces the source's previous one.
     */
    follow(
      revision: WorkspaceViewRevision,
      selection: ChatContextSelection
    ): ReturnType<ChatContextManagerPort['addExplicit']> {
      if (disposed || state.picking$.value)
        return { ok: false, reason: 'unavailable' };
      if (
        !sameScope(selection.scope, scope) ||
        selection.sourceKey !== sourceKey ||
        selection.sourceGeneration !== generation ||
        selection.provenance !== 'automatic'
      )
        return { ok: false, reason: 'invalid' };
      source.followed = { revision, selection };
      const owner = state.contextOwner$.value;
      if (!owner || !sameScope(owner.scope, scope)) return { ok: true };
      return applyFollowed(owner.manager, sourceKey, source.followed);
    },
    /** Whether this source's pick still has its chip on the bound Content. */
    isSelected: (key: string): boolean => {
      const owner = state.contextOwner$.value;
      return (
        !disposed &&
        !!owner &&
        sameScope(owner.scope, scope) &&
        owner.manager.isSelected(key)
      );
    },
    /** Takes this source's pick off the bound Content again: its chip goes with it. */
    unpick(key: string): void {
      const owner = state.contextOwner$.value;
      if (disposed || !owner || !sameScope(owner.scope, scope)) return;
      owner.manager.remove([key]);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      const index = state.pickingSources.indexOf(source);
      if (index !== -1) state.pickingSources.splice(index, 1);
      if (
        sameScope(state.contextOwner$.value?.scope ?? null, scope) &&
        !state.pickingSources.some(
          other =>
            sameScope(other.scope, scope) &&
            other.sourceKey === sourceKey &&
            other.generation === generation
        )
      )
        state.contextOwner$.value?.manager.invalidateSource(
          sourceKey,
          generation
        );
      if (
        sameScope(activeScope, scope) &&
        !state.pickingSources.some(other => sameScope(other.scope, scope))
      )
        state.picking$.next(false);
    },
  };
}

/** Native callbacks use their already registered owner generation. */
export function admitChatPanelContextSelection(
  scope: ChatPanelHostScope,
  selection: ChatContextSelection
): ReturnType<ChatContextManagerPort['addExplicit']> {
  const state = getHostState(scope.workspaceId);
  if (
    !state.picking$.value ||
    !sameScope(scope, activeScope) ||
    !state.contextOwner$.value ||
    !sameScope(state.contextOwner$.value.scope, scope)
  )
    return { ok: false, reason: 'unavailable' };
  if (
    !sameScope(selection.scope, scope) ||
    selection.provenance !== 'explicit' ||
    !state.pickingSources.some(
      source =>
        sameScope(source.scope, scope) &&
        source.sourceKey === selection.sourceKey &&
        source.generation === selection.sourceGeneration
    )
  )
    return { ok: false, reason: 'stale' };
  return state.contextOwner$.value.manager.addExplicit(selection);
}

export function getChatPanelPickingControl(
  scope: ChatPanelHostScope,
  shortcutSettings?: GlobalStateService
): ChatContextPickingControl {
  const bound = { ...scope };
  return {
    scope: bound,
    shortcut: shortcutSettings
      ? {
          getBinding: () => shortcutSettings.selectorShortcut$.value,
          subscribe: listener => {
            const subscription =
              shortcutSettings.selectorShortcut$.subscribe(listener);
            return () => subscription.unsubscribe();
          },
        }
      : undefined,
    canToggle: () => {
      const state = getHostState(bound.workspaceId);
      return (
        sameScope(bound, activeScope) &&
        sameScope(state.contextOwner$.value?.scope ?? null, bound) &&
        state.pickingSources.some(source => sameScope(source.scope, bound))
      );
    },
    isEnabled: () =>
      sameScope(bound, activeScope) &&
      getHostState(bound.workspaceId).picking$.value,
    subscribe: listener => {
      const subscription = pickingState$(bound).subscribe(listener);
      return () => subscription.unsubscribe();
    },
    setEnabled: enabled => setPickingEnabled(bound, enabled),
    bindContext: manager => registerChatPanelContextOwner(bound, manager),
  };
}
/** Attach only after the cached panel's existing properties are initialized. */
export function registerChatPanelBaseHost(
  scope: ChatPanelHostScope,
  panel: HTMLElement | null,
  base: HTMLElement
): () => void {
  if (
    !scope.workspaceId.trim() ||
    !scope.viewId.trim() ||
    !base.isConnected ||
    !belongsToScope(scope, base)
  )
    throw new Error('Unavailable chat panel host');
  const state = getHostState(scope.workspaceId),
    registration: PanelBase = { scope: { ...scope }, element: base, panel };
  if (panel && state.panel && state.panel !== panel)
    throw new Error('Conflicting workspace chat panel');
  state.bases.push(registration);
  if (panel) state.panel = panel;
  attachPanelToActiveHost(scope.workspaceId, state);
  let unregistered = false;
  return () => {
    if (unregistered) return;
    unregistered = true;
    const index = state.bases.indexOf(registration);
    if (index !== -1) state.bases.splice(index, 1);
    if (state.returnFocus && sameScope(state.returnFocus.scope, scope))
      state.returnFocus = null;
    // Effect re-registration must initialize props before reconnecting the panel.
    if (!state.bases.length) state.panel = null;
  };
}
/** The chat panel of `scope`, null where the scope is not the active view. */
export function chatPanelOf(scope: ChatPanelHostScope): HTMLElement | null {
  if (!sameScope(scope, activeScope)) return null;
  return hostStates.get(scope.workspaceId)?.panel ?? null;
}

/** Optional content is the existing surface-owned composer, never another workspace session. */
export function getChatPanelOverlayHost(
  scope: ChatPanelHostScope
): HTMLElement | null {
  if (!sameScope(scope, activeScope)) return null;
  const state = hostStates.get(scope.workspaceId);
  return (
    state?.bases.findLast(
      base =>
        sameScope(base.scope, scope) &&
        base.element.isConnected &&
        belongsToScope(scope, base.element)
    )?.element ?? null
  );
}

export function claimChatPanelHost(
  scope: ChatPanelHostScope,
  element: HTMLElement,
  content?: HTMLElement
): ChatPanelClaimResult {
  if (!sameScope(scope, activeScope) || !belongsToScope(scope, element))
    return { ok: false, reason: 'inactive-view' };
  if (
    !element.isConnected ||
    (content && (!content.isConnected || !belongsToScope(scope, content)))
  )
    return { ok: false, reason: 'unavailable' };
  const state = getHostState(scope.workspaceId);
  if (
    content &&
    (content === state.panel ||
      state.claims.some(claim => claim.content === content))
  )
    return { ok: false, reason: 'unavailable' };
  const claim: Claim = {
    scope: { ...scope },
    element,
    content,
    parent: content?.parentNode ?? null,
    nextSibling: content?.nextSibling ?? null,
  };
  state.claims.push(claim);
  attachPanelToActiveHost(scope.workspaceId, state);
  let released = false;
  return {
    ok: true,
    release: () => {
      if (released) return;
      released = true;
      const index = state.claims.indexOf(claim);
      if (index !== -1) state.claims.splice(index, 1);
      attachPanelToActiveHost(scope.workspaceId, state);
    },
  };
}
export function setChatPanelOverlayVisible(
  scope: ChatPanelHostScope,
  visible: boolean
): ChatPanelHostResult {
  if (!sameScope(scope, activeScope))
    return { ok: false, reason: 'inactive-view' };
  const state = getHostState(scope.workspaceId);
  // A native plan-gated surface uses the same base/visibility authority without
  // constructing or initializing a Copilot panel.
  const base = state.bases.findLast(
    base =>
      sameScope(base.scope, activeScope) &&
      base.element.isConnected &&
      belongsToScope(base.scope, base.element)
  );
  const claim = selectedClaim(state);
  if (!base && !(claim && (state.panel || claim.content)))
    return { ok: false, reason: 'unavailable' };
  if (visible && !state.visible) {
    const focused = nativeActiveElement(
      base?.element.ownerDocument ?? claim!.element.ownerDocument
    );
    state.returnFocus =
      focused && validReturnFocus(scope, focused)
        ? { scope: { ...scope }, element: focused }
        : null;
  }
  const returnFocus = !visible && state.visible ? state.returnFocus : null;
  if (!visible) {
    state.returnFocus = null;
    // A hidden panel keeps its connected owner; picking must not keep
    // swallowing workspace clicks behind a closed overlay.
    state.picking$.next(false);
    if (base) blurChatPanelFocusWithin(base.element);
  }
  state.visible = visible;
  attachPanelToActiveHost(scope.workspaceId, state);
  if (
    returnFocus &&
    sameScope(returnFocus.scope, activeScope) &&
    validReturnFocus(scope, returnFocus.element)
  )
    returnFocus.element.focus({ preventScroll: true });
  return { ok: true };
}
/** Surface claims restore the prior overlay visibility when they end. */
export function isChatPanelOverlayVisible(scope: ChatPanelHostScope): boolean {
  return (
    sameScope(scope, activeScope) &&
    hostStates.get(scope.workspaceId)?.visible === true
  );
}
export function chatPanelOverlayState$(
  workspaceId: string
): Observable<ChatPanelOverlayState> {
  return getHostState(workspaceId).overlay$.pipe(
    distinctUntilChanged(
      (a, b) =>
        sameScope(a.scope, b.scope) &&
        a.visible === b.visible &&
        a.content === b.content &&
        a.surfaceAtBase === b.surfaceAtBase
    )
  );
}
/** Where the view's chat is on screen: overlay surface, a claiming host, or nowhere. */
export type ChatPanelPresence = 'surface' | 'claim' | 'hidden';
export function chatPanelPresence$(
  scope: ChatPanelHostScope
): Observable<ChatPanelPresence> {
  return combineLatest([
    chatPanelOverlayState$(scope.workspaceId),
    chatPanelHostClaimed$(scope),
  ]).pipe(
    map(([overlay, claimed]): ChatPanelPresence => {
      if (overlay.scope?.viewId !== scope.viewId) return 'hidden';
      // A claim away from the base shows the chat in its own host.
      if (claimed && !overlay.surfaceAtBase) return 'claim';
      return overlay.visible ? 'surface' : 'hidden';
    }),
    distinctUntilChanged()
  );
}
export function isChatPanelHostClaimed(scope: ChatPanelHostScope): boolean {
  return (
    sameScope(scope, activeScope) &&
    !!(
      hostStates.get(scope.workspaceId) &&
      selectedClaim(hostStates.get(scope.workspaceId)!)
    )
  );
}
export function chatPanelHostClaimed$(
  scope: ChatPanelHostScope
): Observable<boolean> {
  return getHostState(scope.workspaceId).claimed$.pipe(
    map(() => isChatPanelHostClaimed(scope)),
    distinctUntilChanged()
  );
}
